import React, { useRef, useState, useEffect } from "react";
import { aboutData } from "../data/mock";
import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import { Button } from "../components/ui/button";

function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

export default function AboutPage() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef);
  const servicesRef = useRef(null);
  const servicesInView = useInView(servicesRef);

  return (
    <>
      {/* Hero */}
      <section ref={heroRef} className="pt-32 pb-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div
              className={`transition-all duration-700 ${
                heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
                About
              </span>
              <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-tight text-white mt-2 mb-8">
                {aboutData.headline}
              </h1>
              {aboutData.bio.map((paragraph, i) => (
                <p
                  key={i}
                  className="text-neutral-400 text-base leading-relaxed mb-5"
                >
                  {paragraph}
                </p>
              ))}
              <Link to="/contact" className="inline-block mt-4">
                <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm px-6 py-5 rounded-md group">
                  Work With Me
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>

            {/* Photo collage / decorative */}
            <div
              className={`relative transition-all duration-700 delay-300 ${
                heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className="relative">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1625682115702-3a561cd465fd?w=600&q=80"
                    alt="Photographer portrait"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative frame */}
                <div className="absolute -top-4 -right-4 w-full h-full border-2 border-amber-500/20 rounded-xl -z-10" />
                {/* Badge */}
                <div className="absolute -bottom-6 -left-6 bg-[#141414] border border-[#262626] rounded-xl p-5 shadow-2xl">
                  <div className="flex items-center gap-3">
                    <Camera className="w-8 h-8 text-amber-500" />
                    <div>
                      <p className="font-bebas text-2xl text-white tracking-wider">8+</p>
                      <p className="text-neutral-500 text-xs">Years Behind The Lens</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-20 bg-[#0e0e0e]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutData.stats.map((stat, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-700 ${
                  statsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: statsInView ? `${i * 100 + 100}ms` : "0ms" }}
              >
                <p className="font-bebas text-4xl sm:text-5xl text-amber-500 tracking-wider">
                  {stat.value}
                </p>
                <p className="text-neutral-500 text-sm mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section ref={servicesRef} className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div
            className={`mb-14 transition-all duration-700 ${
              servicesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
              Services
            </span>
            <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-2">
              What I Offer
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {aboutData.services.map((service, i) => (
              <div
                key={i}
                className={`bg-[#141414] border border-[#1e1e1e] rounded-xl p-8 hover:border-amber-500/30 transition-all duration-500 group ${
                  servicesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: servicesInView ? `${i * 120 + 200}ms` : "0ms" }}
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center mb-5 group-hover:bg-amber-500/20 transition-colors duration-300">
                  <span className="font-bebas text-xl text-amber-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-bebas text-2xl tracking-wide text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
